import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProjects } from "@/contexts/ProjectContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import PageHeader from "@/components/dashboard/PageHeader";
import StatusBadge from "@/components/dashboard/StatusBadge";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COLUMNS = [
  { id: "planned", label: "Planned" },
  { id: "in_progress", label: "In progress" },
  { id: "ready_for_review", label: "Ready for review" },
  { id: "published", label: "Published" },
  { id: "needs_update", label: "Needs update" },
];

export default function ContentCalendarPage() {
  const { activeProjectId } = useProjects();
  const [items, setItems] = useState([]);
  const [view, setView] = useState("kanban");

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    const { data } = await api.get(`/content/${activeProjectId}`);
    setItems(data);
  }, [activeProjectId]);
  useEffect(() => { load(); }, [load]);

  const itemsByStatus = useMemo(() => {
    const map = {};
    for (const it of items) {
      (map[it.status] = map[it.status] || []).push(it);
    }
    return map;
  }, [items]);

  const update = async (id, status) => {
    await api.patch(`/content/${id}/status`, { status });
    setItems((it) => it.map((x) => x.id === id ? { ...x, status } : x));
    toast.success("Status updated");
  };

  return (
    <DashboardLayout>
      <PageHeader
        overline="Content Calendar"
        title="Pipeline status, by content piece."
        description="Move pieces from planned to published. Each piece carries its keyword, funnel stage, target, and CTA."
        actions={
          <div className="flex bg-white border border-border rounded-sm overflow-hidden">
            <button
              onClick={() => setView("kanban")}
              data-testid="view-kanban"
              className={`px-4 py-2 text-sm ${view === "kanban" ? "bg-navy-900 text-ivory-50" : "text-navy-900 hover:bg-ivory-100"}`}
            >
              Kanban
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`px-4 py-2 text-sm ${view === "list" ? "bg-navy-900 text-ivory-50" : "text-navy-900 hover:bg-ivory-100"}`}
            >
              List
            </button>
          </div>
        }
      />

      {view === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {COLUMNS.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-sm p-3 min-h-[400px]" data-testid={`column-${c.id}`}>
              <p className="editorial-overline mb-3">{c.label} · {(itemsByStatus[c.id] || []).length}</p>
              <div className="space-y-2">
                {(itemsByStatus[c.id] || []).map((it) => (
                  <Link key={it.id} to={`/content/${it.id}`} className="block border border-border rounded-sm p-3 hover:border-gold-500 cursor-pointer bg-white" data-testid={`content-card-${it.id}`}>
                    <p className="text-sm font-medium leading-snug">{it.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-1.5 truncate">{it.target_keyword}</p>
                    <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <span>{it.funnel_stage}</span>
                      <span>{it.due_date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm" data-testid="content-list-table">
            <thead className="bg-ivory-100 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Keyword</th>
                <th className="px-4 py-3">Funnel</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Impact</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-border hover:bg-ivory-50/50 cursor-pointer" onClick={() => window.location.assign(`/content/${it.id}`)} data-testid={`content-list-row-${it.id}`}>
                  <td className="px-4 py-3 font-medium">{it.title}</td>
                  <td className="px-4 py-3 text-muted-foreground">{it.target_keyword}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest">{it.funnel_stage}</td>
                  <td className="px-4 py-3">{it.due_date}</td>
                  <td className="px-4 py-3 capitalize">{it.estimated_impact}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <Select value={it.status} onValueChange={(v) => update(it.id, v)}>
                      <SelectTrigger className="w-44 h-8 rounded-sm bg-white" data-testid={`content-status-${it.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COLUMNS.map((c) => <SelectItem key={c.id} value={c.id}><StatusBadge status={c.id} /></SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
}
